import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, ShieldAlert, ShieldCheck, AlertTriangle, ArrowRight, Globe, Eye, Download } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import api from '@/lib/api';
import { AnalyticsShell } from '@/components/AnalyticsShell';
import { useUserProfile } from '@/lib/useUserProfile';

const STATUS_COLORS = { allied: '#2E7D32', neutral: '#FF9500', concern: '#FF3B30' };
const STATUS_LABELS = { allied: 'Allied', neutral: 'Neutral', concern: 'Concern' };

const AukusCompliancePage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const { profile } = useUserProfile();

  const handleDownloadAttestation = () => {
    const API = process.env.REACT_APP_BACKEND_URL;
    const company = encodeURIComponent(profile?.company_name || 'Organisation');
    window.open(`${API}/api/analytics/aukus-attestation?company_name=${company}`, '_blank');
  };

  useEffect(() => {
    api.getAukusCompliance()
      .then(res => setData(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <AnalyticsShell><div className="text-center py-20 text-[#4E6974]">Analysing AUKUS compliance...</div></AnalyticsShell>;
  if (!data?.has_data) return <AnalyticsShell><EmptyState /></AnalyticsShell>;

  const filteredSuppliers = filter === 'all' ? data.supplier_compliance : data.supplier_compliance.filter(s => s.compliance_status === filter);
  const tierData = [
    { name: 'Allied nations', value: data.allied_pct, fill: STATUS_COLORS.allied },
    { name: 'Neutral nations', value: data.neutral_pct, fill: STATUS_COLORS.neutral },
    { name: 'Countries of concern', value: data.concern_pct, fill: STATUS_COLORS.concern },
  ];

  return (
    <AnalyticsShell>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-8 mb-6">
        <div className="flex items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-[#0D3B4F]" data-testid="aukus-title">AUKUS supply chain compliance</h1>
            <p className="text-[#4E6974] mt-2">Sovereignty assessment, country-of-concern exposure, and Five Eyes sourcing coverage.</p>
          </div>
          <Button onClick={handleDownloadAttestation} variant="outline" size="sm"
            className="flex items-center gap-2 text-xs border-[#0097A7] text-[#0097A7] hover:bg-[#0097A7]/10" data-testid="aukus-attestation-btn">
            <Download className="h-3.5 w-3.5" /> Generate attestation
          </Button>
        </div>
      </motion.div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <SummaryCard icon={<Shield className="h-5 w-5" />} label="Sovereignty score" value={`${data.overall_sovereignty_score}/100`} color={data.overall_sovereignty_score < 30 ? '#2E7D32' : data.overall_sovereignty_score < 60 ? '#FF9500' : '#FF3B30'} testId="aukus-sov-score" />
        <SummaryCard icon={<ShieldCheck className="h-5 w-5" />} label="Allied sourcing" value={`${data.allied_pct}%`} color="#2E7D32" testId="aukus-allied" />
        <SummaryCard icon={<ShieldAlert className="h-5 w-5" />} label="Concern exposure" value={`${data.concern_pct}%`} color={data.concern_pct > 10 ? '#FF3B30' : '#2E7D32'} testId="aukus-concern" />
        <SummaryCard icon={<Eye className="h-5 w-5" />} label="Five Eyes" value={`${data.five_eyes_pct}%`} color={data.five_eyes_pct > 40 ? '#2E7D32' : '#FF9500'} testId="aukus-fiveeyes" />
        <SummaryCard icon={<Globe className="h-5 w-5" />} label="Suppliers" value={data.total_suppliers} testId="aukus-suppliers" />
      </div>

      {/* Spend allocation bar */}
      <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none mb-6">
        <CardHeader className="pb-2"><CardTitle className="text-base font-bold text-[#0D3B4F]">Spend allocation by sovereignty tier</CardTitle></CardHeader>
        <CardContent>
          <div className="h-12 flex rounded-lg overflow-hidden mb-4" data-testid="aukus-spend-bar">
            {tierData.map(t => t.value > 0 && (
              <div key={t.name} className="flex items-center justify-center text-white text-xs font-semibold" style={{ width: `${t.value}%`, backgroundColor: t.fill, minWidth: t.value > 5 ? 'auto' : '30px' }}>
                {t.value > 8 ? `${t.name} ${t.value}%` : `${t.value}%`}
              </div>
            ))}
          </div>
          <div className="flex gap-6 flex-wrap">
            {tierData.map(t => (
              <div key={t.name} className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: t.fill }} />
                <span className="text-sm text-[#4E6974]">{t.name}: <strong className="text-[#0D3B4F]">{t.value}%</strong></span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Country breakdown chart */}
      <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none mb-6">
        <CardHeader className="pb-2"><CardTitle className="text-base font-bold text-[#0D3B4F]">Spend by country (sovereignty status)</CardTitle></CardHeader>
        <CardContent>
          <div className="h-[350px]" data-testid="aukus-country-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.country_breakdown} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#E0E0E0" />
                <XAxis type="number" fontSize={11} tick={{ fill: '#4E6974' }} tickFormatter={v => `$${(v / 1e6).toFixed(0)}M`} />
                <YAxis type="category" dataKey="country_name" fontSize={11} tick={{ fill: '#4E6974' }} width={120} />
                <Tooltip formatter={v => [`$${(Number(v) / 1e6).toFixed(1)}M`, 'Spend']} />
                <Bar dataKey="spend" radius={[0, 4, 4, 0]}>
                  {data.country_breakdown.map((c, i) => <Cell key={i} fill={STATUS_COLORS[c.status] || '#4E6974'} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Supplier compliance table */}
      <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none mb-6">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-bold text-[#0D3B4F]">Supplier compliance detail</CardTitle>
            <div className="flex gap-1">
              {['all', 'concern', 'neutral', 'allied'].map(f => (
                <Button key={f} size="sm" variant={filter === f ? 'default' : 'outline'}
                  className={filter === f ? 'bg-[#0D3B4F] hover:bg-[#0D3B4F]/90 text-xs' : 'text-xs'}
                  onClick={() => setFilter(f)} data-testid={`aukus-filter-${f}`}>
                  {f === 'all' ? 'All' : STATUS_LABELS[f]}
                </Button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="aukus-supplier-table">
              <thead>
                <tr className="border-b border-[#E0E0E0]">
                  <th className="text-left py-3 px-3 text-[#4E6974] font-medium">Supplier</th>
                  <th className="text-left py-3 px-3 text-[#4E6974] font-medium">Country</th>
                  <th className="text-left py-3 px-3 text-[#4E6974] font-medium">Status</th>
                  <th className="text-right py-3 px-3 text-[#4E6974] font-medium">Spend</th>
                  <th className="text-center py-3 px-3 text-[#4E6974] font-medium">Sov. score</th>
                  <th className="text-center py-3 px-3 text-[#4E6974] font-medium">Five Eyes</th>
                  <th className="text-left py-3 px-3 text-[#4E6974] font-medium">Flags</th>
                </tr>
              </thead>
              <tbody>
                {filteredSuppliers.map(s => (
                  <tr key={s.id} className="border-b border-[#F5F5F5] hover:bg-[#F9FAFB]">
                    <td className="py-3 px-3 font-medium text-[#0D3B4F]">
                      <Link to={`/suppliers/${s.id}`} className="hover:text-[#0097A7]">{s.supplier_name}</Link>
                    </td>
                    <td className="py-3 px-3 text-[#4E6974]">{s.country_name}</td>
                    <td className="py-3 px-3">
                      <Badge className="text-[10px] text-white" style={{ backgroundColor: STATUS_COLORS[s.compliance_status] }}>{STATUS_LABELS[s.compliance_status]}</Badge>
                    </td>
                    <td className="py-3 px-3 text-right text-[#0D3B4F]">${(s.annual_spend / 1e6).toFixed(1)}M</td>
                    <td className="py-3 px-3 text-center">
                      <span className="font-semibold" style={{ color: s.sovereignty_score < 30 ? '#2E7D32' : s.sovereignty_score < 60 ? '#FF9500' : '#FF3B30' }}>{s.sovereignty_score}</span>
                    </td>
                    <td className="py-3 px-3 text-center">{s.five_eyes ? <ShieldCheck className="h-4 w-4 text-[#2E7D32] mx-auto" /> : <span className="text-[#E0E0E0]">-</span>}</td>
                    <td className="py-3 px-3">
                      {s.concern_flags.length > 0 ? (
                        <div className="space-y-1">{s.concern_flags.map((f, i) => <p key={i} className="text-xs text-[#FF3B30]">{f}</p>)}</div>
                      ) : <span className="text-xs text-[#2E7D32]">Clear</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Recommendations */}
      <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none mb-8">
        <CardHeader className="pb-2"><CardTitle className="text-base font-bold text-[#0D3B4F]">AUKUS compliance recommendations</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-3" data-testid="aukus-recommendations">
            {data.recommendations.map((r, i) => (
              <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-[#F5F5F5]">
                <ArrowRight className="h-4 w-4 text-[#0097A7] mt-0.5 flex-shrink-0" />
                <p className="text-sm text-[#0D3B4F]">{r}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </AnalyticsShell>
  );
};

const SummaryCard = ({ icon, label, value, color, testId }) => (
  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
    <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none" data-testid={testId}>
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-2 text-[#4E6974]">{icon}<span className="text-xs uppercase tracking-wide">{label}</span></div>
        <p className="text-3xl font-bold" style={{ color: color || '#0D3B4F' }}>{value}</p>
      </CardContent>
    </Card>
  </motion.div>
);

const EmptyState = () => (
  <div className="text-center py-20 mt-16">
    <Shield className="h-12 w-12 text-[#4E6974] mx-auto mb-4" />
    <h2 className="text-xl font-bold text-[#0D3B4F] mb-2">No supplier data</h2>
    <p className="text-[#4E6974] mb-6">Upload suppliers to view AUKUS compliance.</p>
    <Link to="/suppliers"><Button className="bg-[#00707C] hover:bg-[#00707C]/90">Go to suppliers</Button></Link>
  </div>
);

export default AukusCompliancePage;
